import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../core/components/Button.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Layout from '../../../core/components/Layout.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page/index.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_SAGE200PROFESSIONAL_PATH } from '../../constants.ts';
import { GET_SAGE200PROFESSIONAL_INTEGRATIONS, UPDATE_SAGE200PROFESSIONAL_INTEGRATION } from '../../queries.ts';

const FORM_DEFAULT_VALUES = {
    name: '',
    siteId: '',
    companyId: '',
    warehouseId: '',
    lineTypeId: '',
    defaultTaxCodeId: '',
    authState: '',
    orderChannelIds: [],
};

const Sage200IntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')} / ${t('sage200Professional.title')}`;

    const { data, refetch } = useQuery(GET_SAGE200PROFESSIONAL_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.sage200professionalIntegrations?.find((i) => i.id === integrationId);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                siteId: integration.siteId,
                companyId: integration.companyId,
                warehouseId: integration.warehouseId,
                lineTypeId: integration.lineTypeId,
                defaultTaxCodeId: integration.defaultTaxCodeId,
                authState: integration.authState,
                orderChannelIds: integration.orderChannels.map((c) => c.id),
            });
        }
    }, [integration]);

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_SAGE200PROFESSIONAL_INTEGRATION);

    const handleSubmit = async (formData) => {
        try {
            await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    siteId: formData.siteId,
                    companyId: formData.companyId,
                    warehouseId: formData.warehouseId,
                    lineTypeId: formData.lineTypeId,
                    defaultTaxCodeId: formData.defaultTaxCodeId,
                    authState: formData.authState,
                    orderChannels: formData.orderChannelIds,
                },
            });
            await refetch();
            publishToast({ description: t('sage200Professional.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_SAGE200PROFESSIONAL_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('sage200Professional.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            {
                                label: t('sage200Professional.title'),
                                to: url(CUSTOMER_INTEGRATION_SAGE200PROFESSIONAL_PATH),
                            },
                            { label: integration?.name || t('sage200Professional.form.updateTitle') },
                        ]}
                    />
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard
                        onSubmit={form.handleSubmit(handleSubmit)}
                        title={t('sage200Professional.form.updateTitle')}
                    >
                        <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sage200Professional.form.name')} error={formErrors.name}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="siteId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sage200Professional.form.siteId')} error={formErrors.siteId}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="companyId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sage200Professional.form.companyId')} error={formErrors.companyId}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="warehouseId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sage200Professional.form.warehouseId')}
                                    error={formErrors.warehouseId}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="lineTypeId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sage200Professional.form.lineTypeId')}
                                    error={formErrors.lineTypeId}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="defaultTaxCodeId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sage200Professional.form.defaultTaxCodeId')}
                                    error={formErrors.defaultTaxCodeId}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="authState"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sage200Professional.form.authState')} error={formErrors.authState}>
                                    <StringField {...field} readOnly={true} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sage200Professional.form.orderChannelIds')}
                                    error={formErrors.orderChannelIds}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_INTEGRATION_SAGE200PROFESSIONAL_PATH)}>
                                    {t('sage200Professional.form.cancelButton')}
                                </Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default Sage200IntegrationEditPage;
